import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X } from "lucide-react";

/**
 * FriendRequestModal — sends a friend request with an optional 140-char note.
 * Backend enforces the 140-char cap; we mirror it here so the counter feels
 * honest and the send button locks past the limit.
 */
const MAX = 140;

export default function FriendRequestModal({ target, onClose, onSent }) {
  const [msg, setMsg] = useState("");
  const [sending, setSending] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    // Autofocus the textarea when the modal opens so it feels lightweight.
    const t = setTimeout(() => inputRef.current?.focus(), 60);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    // ESC closes the modal — matches the rest of the app's modal ergonomics.
    function onKey(e) { if (e.key === "Escape") onClose?.(); }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Hide the global Add FAB while the modal is open.
  useEffect(() => {
    document.body.classList.add("modal-open");
    return () => document.body.classList.remove("modal-open");
  }, []);

  async function send() {
    if (sending) return;
    setSending(true);
    try {
      await api.post("/commons/friend-requests", {
        to_username: target.username,
        message: msg.trim().slice(0, MAX),
      });
      toast.success("request sent");
      onSent?.();
    } catch (e) {
      const detail = e?.response?.data?.detail || "could not send request";
      toast.error(detail);
    } finally {
      setSending(false);
    }
  }

  const remaining = MAX - msg.length;
  const over = remaining < 0;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-4 py-8 bg-[rgba(0,0,0,0.7)] backdrop-blur-sm"
      onClick={onClose}
      data-testid="friend-request-modal-backdrop"
    >
      <div
        className="w-full max-w-md placed-card p-6 relative"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        data-testid="friend-request-modal"
      >
        <button
          type="button"
          onClick={onClose}
          data-testid="friend-request-close"
          className="absolute top-3 right-3 text-[var(--cream-mute)] hover:text-[var(--brass)] transition-colors"
          aria-label="close"
        >
          <X size={16} strokeWidth={1.5} />
        </button>

        <p className="font-mono-lc text-[10px] tracking-[0.24em] uppercase text-[var(--brass)] opacity-80">
          send a friend request
        </p>
        <h2 className="mt-2 font-serif-lit text-2xl text-[var(--cream)]">
          to {target.name || `@${target.username}`}
        </h2>
        <p className="mt-1 font-mono-lc text-[10px] tracking-[0.14em] text-[var(--cream-mute)]">
          @{target.username}
        </p>

        <label className="block mt-6">
          <span className="font-mono-lc text-[10px] tracking-[0.24em] uppercase text-[var(--cream-dim)]">
            a short note — optional
          </span>
          <textarea
            ref={inputRef}
            value={msg}
            onChange={(e) => setMsg(e.target.value)}
            placeholder="say hello, or leave it blank…"
            rows={3}
            data-testid="friend-request-message"
            className="mt-2 w-full bg-[var(--bg)] border border-[rgba(179,137,90,0.24)] rounded p-3 font-garamond italic text-[var(--cream)] placeholder:text-[var(--cream-mute)] focus:outline-none focus:border-[var(--brass)] resize-none"
          />
        </label>
        <p
          className={`mt-1 font-mono-lc text-[10px] tracking-[0.14em] text-right ${over ? "text-[#C86A5C]" : "text-[var(--cream-mute)]"}`}
          data-testid="friend-request-counter"
        >
          {remaining} left
        </p>

        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            data-testid="friend-request-cancel"
            className="lc-btn-ghost"
          >
            never mind
          </button>
          <button
            type="button"
            onClick={send}
            disabled={sending || over}
            data-testid="friend-request-send"
            className="lc-btn"
          >
            {sending ? "sending…" : "send request"}
          </button>
        </div>
      </div>
    </div>
  );
}
